import { forwardRef, type ComponentPropsWithoutRef } from "react";
import { useTranslation } from "react-i18next";
import { MoreHorizontal } from "lucide-react";
import { cn, Tooltip, TooltipContent, TooltipTrigger } from "@nexus/ui";

type TriggerVariant = "visible" | "hover-group" | "hover-group-member";

interface RowActionsTriggerProps extends ComponentPropsWithoutRef<"button"> {
  /**
   * "visible" — always shown (drawer/table headers), h-7.
   * "hover-group" — fades in on the default `group` hover (table rows), h-7.
   * "hover-group-member" — fades in on the `group/member` hover (drawer member rows), h-6.
   */
  variant?: TriggerVariant;
}

const VARIANT_CLASS: Record<TriggerVariant, string> = {
  visible:
    "h-7 w-7 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
  "hover-group": "h-7 w-7 opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100",
  "hover-group-member": "h-6 w-6 shrink-0 opacity-0 transition-opacity hover:bg-muted group-hover/member:opacity-100",
};

const ICON_CLASS: Record<TriggerVariant, string> = {
  visible: "h-3.5 w-3.5",
  "hover-group": "h-3.5 w-3.5 text-muted-foreground",
  "hover-group-member": "h-3.5 w-3.5 text-muted-foreground",
};

/**
 * `MoreHorizontal` trigger button for row/member action dropdowns. Single
 * source for the button markup previously copy-pasted across the People,
 * Roles, and Invites tables plus the Org/Role drawer member rows and headers.
 *
 * Forwards its ref and spreads incoming props so it works as the `asChild`
 * target of `DropdownMenuTrigger` — Radix's `Slot` clones this element to
 * attach the click/keyboard handlers and ref it needs to open the menu. The
 * tooltip's own `asChild` nests around that same button and composes its
 * hover handlers on top, rather than fighting over a single trigger.
 */
export const RowActionsTrigger = forwardRef<HTMLButtonElement, RowActionsTriggerProps>(
  ({ variant = "visible", className, ...props }, ref) => {
    const { t } = useTranslation();
    const label = t("iam.shared.moreActions", "More actions");

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            ref={ref}
            className={cn("flex items-center justify-center rounded-md", VARIANT_CLASS[variant], className)}
            aria-label={label}
            {...props}
          >
            <MoreHorizontal className={ICON_CLASS[variant]} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    );
  },
);
RowActionsTrigger.displayName = "RowActionsTrigger";
